import { checkRevelationEmailState } from '@aibees/elephant/components/people-contact-data/utils';
import { useIsPeopleUnlockButtonLoading } from '@aibees/elephant/components/people-unlock/hooks/use-is-people-unlock-button-loading';
import { QUICK_INFO_BUTTONS } from '@aibees/elephant/components/quick-info-group/components/quick-info-button';
import {
  ProcessingItemState,
  useActionButtonContext,
} from '@aibees/elephant/context/action-button-context';
import { useSearchTypeFromRoute, useTrackEvents } from '@aibees/elephant/hooks';
import {
  SaveRecordAction,
  SaveRecordRequest,
} from '@aibees/elephant/hooks/types';
import { useSaveRecord } from '@aibees/elephant/hooks/use-save-record';
import { usePersonUpdatesStore } from '@aibees/elephant/store/person-updates';
import { SearchType } from '@aibees/elephant/types';
import { notificationIconMap } from '@aibees/elephant/utils';
import { useAuthContext } from '@aibees/shared/auth';
import { CreditUsageType } from '@aibees/shared/definition';
import { useNotify } from '@aibees/shared/hooks';
import { baseQueryClient } from '@aibees/shared/http';
import { useWorkspaceUsers } from '@aibees/shared/workspace';
import { random } from 'lodash-es';
import { MouseEventHandler } from 'react';
import { PeopleTableInfo } from '../../../types';
import { doesEmailEnrichmentHaveAnyUpdates } from '../../people-quick-info/utils';
import {
  isAnyEmailNeedsReverification,
  isEmailBelongsToPreviousCompanies,
} from '../utils';

(window as any).baseQueryClient = baseQueryClient;

export function useHandleUnlockEmails(item?: PeopleTableInfo) {
  const users = useWorkspaceUsers(true);
  const { user } = useAuthContext();
  const selectedUser = users.find((u) => u.id === user?.id);
  const searchType = useSearchTypeFromRoute();
  const { track } = useTrackEvents();
  const { mutateAsync } = useSaveRecord(SearchType.People);
  const notify = useNotify();
  const { emailLoading } = useIsPeopleUnlockButtonLoading(item);
  const unlocked = checkRevelationEmailState(item);
  const { setEmailsUpdateTriggered } = usePersonUpdatesStore();
  const { addProcessingItems } = useActionButtonContext();
  const hasUpdate = doesEmailEnrichmentHaveAnyUpdates(item);
  const needToCheckForUpdate = isEmailBelongsToPreviousCompanies(item);
  const showReverify =
    !hasUpdate &&
    !needToCheckForUpdate &&
    item?.revelation?.email &&
    isAnyEmailNeedsReverification(item?.revelation?.email);
  const creditType = hasUpdate
    ? CreditUsageType.CONTACT_EMAIL_REVERIFICATION
    : needToCheckForUpdate
    ? CreditUsageType.CONTACT_PROFILE_UPDATE
    : showReverify
    ? CreditUsageType.CONTACT_EMAIL_REVERIFICATION
    : CreditUsageType.CONTACT_EMAIL_FINDING;

  const handleReverify = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation();
    e.preventDefault();
    if (item?.id) {
      setEmailsUpdateTriggered(true);
      addProcessingItems({
        id: item.id,
        type: QUICK_INFO_BUTTONS.EMAIL_ENRICHMENT,
        state: ProcessingItemState.REVERIFYING,
      });
      const key = Date.now().toString();

      mutateAsync({
        kind: 'ADD',
        ids: [item?.id],
        actions: [SaveRecordAction.CONTACT_REVERIFY_EMAIL],
        overwriteOwner: false,
        user: selectedUser,
        labels: [],
      }).then(() => {
        notify.progress(
          'Your request is in progress',
          'Processing the contact details. Please wait...',
          key,
          {
            icon: notificationIconMap.ContactDetails,
          }
        );
        track('UNLOCK_EMAIL', { multiple: false, reverify: true, searchType });
      });
    }
  };

  const handleCheckForUpdate: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (item?.id) {
      setEmailsUpdateTriggered(true);
      addProcessingItems({
        id: item.id,
        type: QUICK_INFO_BUTTONS.EMAIL_ENRICHMENT,
        state: ProcessingItemState.CHECKING_FOR_UPDATE,
      });
      addProcessingItems({
        id: item.id,
        type: QUICK_INFO_BUTTONS.CONTACT_ENRICHMENT,
        state: ProcessingItemState.UPDATING,
      });

      mutateAsync({
        kind: 'ADD',
        ids: [item?.id],
        actions: [SaveRecordAction.CONTACT_UPDATE_JOB_CHANGE],
        overwriteOwner: false,
        user: selectedUser,
        labels: [],
      }).then(() => {
        notify.complete(
          'Your request is in progress',
          'Processing the contact details. Please wait...',
          random(1, 101),
          {
            icon: notificationIconMap.ContactDetails,
          }
        );
        track('UNLOCK_EMAIL', { multiple: false, update: true, searchType });
      });
    }
  };

  const handleUpdate: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (item?.id) {
      setEmailsUpdateTriggered(true);
      addProcessingItems({
        id: item.id,
        type: QUICK_INFO_BUTTONS.EMAIL_ENRICHMENT,
        state: ProcessingItemState.UPDATING,
      });

      mutateAsync({
        kind: 'ADD',
        ids: [item?.id],
        actions: [SaveRecordAction.CONTACT_REVEAL_EMAIL],
        overwriteOwner: false,
        user: selectedUser,
        labels: [],
      }).then(() => {
        notify.complete(
          'Your request is in progress',
          'Processing the contact details. Please wait...',
          random(1, 101),
          {
            icon: notificationIconMap.ContactDetails,
          }
        );
        track('UNLOCK_EMAIL', { multiple: false, update: true, searchType });
      });
    }
  };

  const handleUnlock: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (emailLoading) return;

    if (item?.id) {
      const payload: SaveRecordRequest = {
        kind: 'ADD',
        actions: [SaveRecordAction.CONTACT_REVEAL_EMAIL],
        ids: [item?.id],
        overwriteOwner: false,
        user: selectedUser,
        labels: [],
      };
      addProcessingItems({
        id: item.id,
        type: QUICK_INFO_BUTTONS.EMAIL_ENRICHMENT,
        state: ProcessingItemState.UNLOCKING,
      });
      const key = Date.now().toString();

      mutateAsync(payload).then((data) => {
        notify.progress(
          'Your request is in progress',
          'Processing the contact details. Please wait...',
          key,
          {
            icon: notificationIconMap.ContactDetails,
          }
        );
        track('UNLOCK_EMAIL', { multiple: false, sidebar: false, searchType });
      });
    }
  };

  return {
    handleReverify,
    handleUpdate,
    handleCheckForUpdate,
    hasUpdate,
    needToCheckForUpdate,
    showReverify,
    unlocked,
    handleUnlock,
    loading: emailLoading,
    creditType,
  };
}
